import { createContext, useContext, useEffect, useRef, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { normalizeAuthUser } from "../lib/auth";
import { API_BASE } from "../lib/api";

const AuthContext = createContext();
const IDLE_TIMEOUT_MS = 20 * 60 * 1000;

export function AuthProvider({ children }) {
  const navigate = useNavigate();
  const storedToken = localStorage.getItem("token");
  const storedUser = (() => {
    try {
      return JSON.parse(localStorage.getItem("user"));
    } catch {
      return null;
    }
  })();

  const [token, setToken] = useState(storedToken);
  const [user, setUser] = useState(normalizeAuthUser(storedUser, storedToken));
  const idleTimerRef = useRef(null);

  const login = (data) => {
    const nextToken = data?.token || "";
    const nextUser = normalizeAuthUser(data?.user, nextToken);

    setUser(nextUser);
    setToken(nextToken);
    localStorage.setItem("user", JSON.stringify(nextUser));
    localStorage.setItem("token", nextToken);
    window.dispatchEvent(new Event("auth:changed"));
    sessionStorage.removeItem("session_timeout");
  };

  const logout = (reason = "manual") => {
    setUser(null);
    setToken(null);
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    window.dispatchEvent(new Event("auth:changed"));

    if (reason === "timeout") {
      sessionStorage.setItem("session_timeout", "1");
      navigate("/session-timeout", { replace: true });
      return;
    }

    sessionStorage.removeItem("session_timeout");
    window.location.assign("/");
  };

  useEffect(() => {
    if (!user || !token) return undefined;

    const resetTimer = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => logout("timeout"), IDLE_TIMEOUT_MS);
    };

    const events = ["mousedown", "keydown", "touchstart", "scroll"];
    events.forEach((evt) => window.addEventListener(evt, resetTimer, { passive: true }));
    resetTimer();

    return () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      events.forEach((evt) => window.removeEventListener(evt, resetTimer));
    };
  }, [user, token]);

  useEffect(() => {
    if (!token) return;

    let cancelled = false;

    fetch(`${API_BASE}/api/users/me`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (res) => {
        if (!res.ok) return null;
        const data = await res.json();
        return data;
      })
      .then((data) => {
        if (!data || cancelled) return;
        const nextUser = normalizeAuthUser(data, token);
        setUser(nextUser);
        localStorage.setItem("user", JSON.stringify(nextUser));
        window.dispatchEvent(new Event("auth:changed"));
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [token]);

  const value = useMemo(
    () => ({ user, token, login, logout }),
    [user, token]
  );
  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
